"use client"

import * as React from "react"
import { Check } from "lucide-react"
import { cn } from "@/lib/utils"

export interface CheckboxProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'checked' | 'onChange'> {
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  children?: React.ReactNode
}

const Checkbox = React.forwardRef<HTMLInputElement, CheckboxProps>(
  ({ className, checked, onCheckedChange, children, id, ...props }, ref) => {
    const checkboxId = id || React.useId()
    
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      onCheckedChange?.(e.target.checked)
    }

    return (
      <label htmlFor={checkboxId} className={cn("inline-flex items-center", !children && "cursor-pointer")}>
        <input
          type="checkbox"
          ref={ref}
          id={checkboxId}
          checked={checked}
          onChange={handleChange}
          className="peer sr-only"
          {...props}
        />
        <div
          className={cn(
            "grid place-content-center peer h-4 w-4 shrink-0 rounded-sm border border-slate-200 border-slate-900 shadow focus-within:outline-none focus-within:ring-1 focus-within:ring-slate-950 disabled:cursor-not-allowed disabled:opacity-50 dark:border-slate-800 dark:border-slate-50 dark:focus-within:ring-slate-300",
            checked && "bg-slate-900 text-slate-50 dark:bg-slate-50 dark:text-slate-900",
            className
          )}
          aria-hidden="true"
        >
          {checked && (
            <Check className="h-4 w-4 text-current" />
          )}
        </div>
        {children && <span className="ml-2 text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70">{children}</span>}
      </label>
    )
  }
)
Checkbox.displayName = "Checkbox"

export { Checkbox }
